import React, { useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material"; // Import eye icons
import { createUser } from "../api/createUser";
import storage from "../../../utils/storage";
import { notification } from "antd"; // Import Ant Design notification
import { useNavigate } from "react-router-dom"; // Import useNavigate for redirection

const RegisterPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [error, setError] = useState("");
  const [isNextClicked, setIsNextClicked] = useState(false); // Track if "Next" is clicked
  const [showPassword, setShowPassword] = useState(false); // Track password visibility

  const steps = ["إنشاء حساب", "معلومات الاتصال", "معلومات العنوان"];
  const navigate = useNavigate(); // Initialize useNavigate

  const handleNext = () => {
    setIsNextClicked(true); // Set "Next" button clicked to true

    // Validate password length on the first step
    if (activeStep === 0 && password.length < 8) {
      setError("كلمة المرور يجب أن تكون على الأقل 8 أحرف.");
      return; // Stop proceeding to the next step
    }

    setError(""); // Clear any previous errors
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name,
        username,
        password,
        phone,
        address,
        landmark,
      };

      // Sending the data to the API
      const response = await createUser(data);

      // Check if the response contains validation errors
      if (response.status === "validation_error") {
        // Handling the validation errors returned from the API
        if (response.errors.username) {
          setError("اسم المستخدم موجود بالفعل. يرجى اختيار اسم مستخدم آخر.");
        } else {
          // Other validation errors
          setError("حدث خطأ في إرسال البيانات. الرجاء المحاولة لاحقاً.");
        }
        setActiveStep(0); // Reset to the first step
      } else {
        // Handle success case here (if no validation errors)
        storage.setToken(response.token);

        // Show success notification
        notification.success({
          message: "تم التسجيل بنجاح",
          description:
            "تم إنشاء حسابك بنجاح. سيتم تحويلك إلى صفحة تسجيل الدخول.",
          placement: "topRight", // Position of the notification
        });

        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate("//registrate/login"); // Replace "/login" with your login route
        }, 3000);
      }
    } catch (err: any) {
      // If an error occurs that is not related to the validation error,
      // we log the error and show a generic error message.
      console.error("Unexpected error:", err);
      if (err.response.data.errors.username) {
        setError("اسم المستخدم موجود بالفعل. يرجى اختيار اسم مستخدم آخر.");
        setActiveStep(0);
      } else {
        setError("حدث خطأ في إرسال البيانات. الرجاء المحاولة لاحقاً.");
      }
    }
  };

  // Step 1 validation
  const isAccountComplete = name && username && password;

  // Step 2 validation
  const isContactComplete = phone;

  // Step 3 validation
  const isAddressComplete = address && landmark;

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Box
      sx={{
        maxWidth: 400,
        mx: "auto",
        padding: 2,
        marginTop: "10px",
        direction: "rtl", // Apply right-to-left direction
      }}
    >
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          display: "flex",
          flexDirection: "row-reverse", // Reverse the direction of the steps
          justifyContent: "flex-start", // Align to the right
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ mt: 3 }}>
        {activeStep === 0 ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              إنشاء حساب
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">الاسم الكامل</Typography>
              <TextField
                placeholder="الاسم"
                value={name}
                onChange={(e: any) => setName(e.target.value)}
                dir="rtl"
                fullWidth
              />
            </Box>
            <Box sx={{ mb: 4 }}>
              <Typography variant="body1">اسم المستخدم</Typography>
              <TextField
                placeholder="اسم المستخدم"
                value={username}
                onChange={(e: any) => setUsername(e.target.value)}
                dir="rtl"
                fullWidth
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">كلمة المرور</Typography>
              <TextField
                placeholder="كلمة المرور"
                type={showPassword ? "text" : "password"} // Toggle password visibility
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                dir="rtl"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{ marginLeft: "8px" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/* Show password length validation message only after "Next" is clicked */}
              {isNextClicked && password.length < 8 && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  كلمة المرور يجب أن تكون على الأقل 8 أحرف.
                </Typography>
              )}
            </Box>
            <div className="flex justify-end w-full">
              {" "}
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!isAccountComplete} // Disable only if required fields are empty
              >
                التالي
              </Button>
            </div>
          </Box>
        ) : activeStep === 1 ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              معلومات الاتصال
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">رقم الهاتف</Typography>
              <TextField
                placeholder="رقم الهاتف"
                type="tel" // Ensures the phone number keyboard appears on mobile devices
                value={phone}
                onChange={(e: any) => {
                  const value = e.target.value.replace(/[^\d]/g, ""); // Remove non-numeric characters
                  setPhone(value);
                }}
                dir="rtl"
                fullWidth
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="outlined" color="secondary" onClick={handleBack}>
                العودة
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={!isContactComplete}
              >
                التالي
              </Button>
            </Box>
          </Box>
        ) : activeStep === 2 ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              معلومات العنوان
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">العنوان</Typography>
              <TextField
                placeholder="العنوان"
                value={address}
                onChange={(e: any) => setAddress(e.target.value)}
                dir="rtl"
                fullWidth
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">علامة مميزة</Typography>
              <TextField
                placeholder="علامة مميزة"
                value={landmark}
                onChange={(e: any) => setLandmark(e.target.value)}
                dir="rtl"
                fullWidth
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button variant="outlined" color="secondary" onClick={handleBack}>
                العودة
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!isAddressComplete}
              >
                إرسال
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default RegisterPage;
